import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { getFreightFaqCreateMeta, getFreightFaqEdit, storeFreightFaq, updateFreightFaq } from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const FreightFaqFormPage = () => {
  const navigate = useNavigate();
  const newMatch = useMatch({ path: "/freight/faqs/new", end: true });
  const { id: routeId } = useParams();
  const isNew = Boolean(newMatch);
  const faqId = isNew ? null : routeId;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "FAQs", link: "/freight/faqs" },
      { label: isNew ? "New FAQ" : `Edit #${faqId ?? ""}` }
    ],
    [roleLabel, isNew, faqId]
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sectionList, setSectionList] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [status, setStatus] = useState("1");

  const loadNewMeta = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreightFaqCreateMeta();
      const list = Array.isArray(res?.data?.freightFaqSectionList) ? res.data.freightFaqSectionList : [];
      setSectionList(list);
      const firstActive = list.find((s) => String(s.status) === "1");
      if (firstActive) setSectionId(String(firstActive.id));
      else if (list[0]) setSectionId(String(list[0].id));
    } catch (e) {
      showError(e.message || "Failed to load sections");
      setSectionList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEdit = useCallback(async () => {
    if (!faqId) return;
    setLoading(true);
    try {
      const res = await getFreightFaqEdit(faqId);
      const d = res?.data?.freightFaqData;
      const list = Array.isArray(res?.data?.freightFaqSectionList) ? res.data.freightFaqSectionList : [];
      if (!d) throw new Error("FAQ not found");
      setSectionList(list);
      setSectionId(String(d.section_id ?? ""));
      setOrderNo(d.order_no != null ? String(d.order_no) : "");
      setQuestion(String(d.question ?? ""));
      setAnswer(String(d.answer ?? ""));
      setStatus(String(d.status ?? "1"));
    } catch (e) {
      showError(e.message || "Load failed");
      navigate("/freight/faqs", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [faqId, navigate]);

  useEffect(() => {
    if (isNew) loadNewMeta();
    else loadEdit();
  }, [isNew, loadEdit, loadNewMeta]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const sid = Number(sectionId);
    if (!Number.isFinite(sid) || sid <= 0) {
      showError("Please choose a section");
      return;
    }
    const q = question.trim();
    if (!q) {
      showError("Question is required");
      return;
    }
    setSubmitting(true);
    try {
      if (isNew) {
        await storeFreightFaq({
          section_id: sid,
          order_no: orderNo === "" ? undefined : Number(orderNo),
          question: q,
          answer: answer.trim(),
          status
        });
        showSuccess("FAQ created");
      } else {
        await updateFreightFaq({
          id: Number(faqId),
          section_id: sid,
          order_no: orderNo === "" ? undefined : Number(orderNo),
          question: q,
          answer: answer.trim(),
          status
        });
        showSuccess("FAQ updated");
      }
      navigate("/freight/faqs");
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumbs={breadcrumbs}>
        <div className="max-w-[800px] card offers-table p-6">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[800px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="m-0">{isNew ? "New FAQ" : "Edit FAQ"}</h3>
            <Link to="/freight/faqs" className="btn-outline no-underline text-sm">
              ← Back to list
            </Link>
          </div>

          {sectionList.length === 0 ? (
            <p className="text-sm text-gray-600">
              No sections available.{" "}
              <Link to="/freight/faq_section/new" className="text-[#064E8D] underline">
                Create a FAQ section
              </Link>{" "}
              first.
            </p>
          ) : (
            <form className="freight-admin-form space-y-4" onSubmit={onSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
                <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} required>
                  <option value="" disabled>
                    Select…
                  </option>
                  {sectionList.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                      {String(s.status) !== "1" ? " (inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
                <input
                  type="number"
                  min={0}
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
                <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                <textarea
                  rows={6}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Shown to users in the app"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="submit" className="btn-primary" disabled={submitting || sectionList.length === 0}>
                  {submitting ? "Saving…" : isNew ? "Create FAQ" : "Save changes"}
                </button>
                <Link to="/freight/faqs" className="btn-outline no-underline inline-flex items-center">
                  Cancel
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightFaqFormPage;
